import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "@/components/ui/button";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { trpc } from "@/lib/trpc";

/* ─── Affiliate Hero — Floating Icons ─────────────────────────── */
const AFFILIATE_ICONS = [
  { emoji: "💵", startX: "-5%",  startY: "25%", endX: "105%", endY: "20%", dur: 30, delay: 0,  size: 58 },
  { emoji: "💻", startX: "90%",  startY: "10%", endX: "85%",  endY: "85%", dur: 34, delay: 4,  size: 62 },
  { emoji: "📱", startX: "5%",   startY: "75%", endX: "8%",   endY: "10%", dur: 38, delay: 8,  size: 52 },
  { emoji: "✈️", startX: "105%", startY: "55%", endX: "-5%",  endY: "60%", dur: 32, delay: 2,  size: 56 },
  { emoji: "🌴", startX: "50%",  startY: "-8%", endX: "55%",  endY: "108%",dur: 40, delay: 14, size: 50 },
  { emoji: "🚢", startX: "-5%",  startY: "88%", endX: "108%", endY: "78%", dur: 36, delay: 6,  size: 56 },
  { emoji: "💵", startX: "75%",  startY: "108%",endX: "70%",  endY: "-8%", dur: 42, delay: 18, size: 48 },
  { emoji: "📱", startX: "108%", startY: "30%", endX: "-5%",  endY: "28%", dur: 28, delay: 10, size: 50 },
  { emoji: "💻", startX: "25%",  startY: "108%",endX: "20%",  endY: "-8%", dur: 44, delay: 22, size: 46 },
];

function AffiliateFloatingIcons() {
  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none select-none">
      {AFFILIATE_ICONS.map((item, i) => (
        <motion.div
          key={i}
          className="absolute"
          initial={{ left: item.startX, top: item.startY, opacity: 0 }}
          animate={{
            left: [item.startX, item.endX],
            top:  [item.startY, item.endY],
            opacity: [0, 0.75, 0.75, 0],
          }}
          transition={{
            duration: item.dur,
            repeat: Infinity,
            ease: "linear",
            delay: item.delay,
            times: [0, 0.08, 0.92, 1],
          }}
        >
          <div
            style={{
              position: "relative",
              width: item.size,
              height: item.size,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              borderRadius: "16px",
              background: "linear-gradient(135deg, rgba(124,58,237,0.22) 0%, rgba(79,70,229,0.14) 100%)",
              boxShadow: "0 0 20px rgba(124,58,237,0.2), inset 0 1px 0 rgba(167,139,250,0.15)",
            }}
          >
            <span
              style={{
                fontSize: item.size * 0.5,
                lineHeight: 1,
                filter: "sepia(0.6) hue-rotate(220deg) saturate(1.8) brightness(0.85)",
                display: "block",
              }}
            >
              {item.emoji}
            </span>
            <div
              style={{
                position: "absolute",
                inset: 0,
                borderRadius: "16px",
                background: "rgba(124,58,237,0.28)",
                mixBlendMode: "color",
                pointerEvents: "none",
              }}
            />
          </div>
        </motion.div>
      ))}
    </div>
  );
}

/* ─── Types ─────────────────────────────────────────── */
type Plan = "6month" | "12month";

interface RegForm {
  name: string;
  phone: string;
  email: string;
}

/* ─── Data ───────────────────────────────────────────── */
const BENEFITS = [
  {
    icon: "💰",
    title: "Нэмэлт орлого",
    desc: "Сард 3,000,000₮ хүртэлх нэмэлт орлого олох боломж. Цаг, байршил харгалзахгүй гэрээсээ ажиллана.",
  },
  {
    icon: "🛂",
    title: "Өөрийн визийг өөрөө мэдүүлэх",
    desc: "Сургалтын дараа та өөрийн болон гэр бүлийнхнийхээ визийг бие даан мэдүүлэх мэдлэгтэй болно.",
  },
  {
    icon: "🎁",
    title: "Үнэ төлбөргүй визний үйлчилгээ",
    desc: "6 сар идэвхтэй ажилласан нөхцөлд манай байгууллагаар үнэ төлбөргүй визний үйлчилгээ авах эрхтэй болно.",
  },
  {
    icon: "🎓",
    title: "Хөнгөлөлттэй сургалт",
    desc: "АНУ, Канад, Ирланд дахь гэрээт сургуулиудад хөнгөлөлттэй үнээр суралцах боломж.",
  },
  {
    icon: "✈️",
    title: "АНУ-руу урилгатай аялал",
    desc: "Манай байгууллагын хөтөлбөрт аялалд АНУ-руу урилгатай аялах боломж (болзол хангасан нөхцөлд).",
  },
  {
    icon: "📜",
    title: "Албан ёсний гэрчилгээ",
    desc: "Нэг өдрийн онлайн сургалтаар United Dream Mongolia-ийн албан ёсний гэрчилгээтэй агент болно.",
  },
  {
    icon: "📱",
    title: "Хүссэн газраасаа цагийн хамааралгүй",
    desc: "Гэртээ, оффис, аялал — харгалзахгүй. Танд утас байхад л болно. Цагийн хамааралгүй, өөрийн цагтаа чөлөөтэй ажиллах боломж.",
  },
];

const HOW_IT_WORKS = [
  {
    step: "01",
    title: "Бүртгүүлж, сургалтанд суу",
    desc: "Онлайнаар бүртгүүлж, нэг өдрийн сургалтанд суу. Маргаашнаас нь шууд ажиллаж эхлэх боломжтой.",
  },
  {
    step: "02",
    title: "Өөрийн referral code авна",
    desc: "Зөвхөн танд зориулсан давтагдашгүй referral code авна. Энэ кодоор таниар дамжиж ирсэн хүмүүс хөнгөлөлттэй үйлчилгээ авна.",
  },
  {
    step: "03",
    title: "Хүмүүст санал болго",
    desc: "Манай маркетингийн материал эсвэл өөрийн сошиал медиа ашиглан бүтээгдэхүүнийг хүмүүст санал болго.",
  },
  {
    step: "04",
    title: "Комисс авна",
    desc: "Таны кодоор ирсэн хүн үйлчилгээ авах бүрт үйлчилгээний хөлсний 10–50%-ийг та авна.",
  },
];

const CURRICULUM = [
  "🇺🇸 АНУ-ын B1/B2, F1 визний мэдүүлгийн дэлгэрэнгүй мэдлэг",
  "🇨🇦 Канадын TRV, суралцах визний онцлог",
  "🇮🇪 Ирландын аяллын болон сурах визний мэдлэг",
  "🇦🇺 Австралийн виз, Subclass 600 мэдлэг",
  "🇪🇺 Шенгений улсуудын виз мэдүүлэх зааварчилгаа",
  "🏫 Гэрээт сургуулиудын мэдээлэл, хөнгөлөлт",
  "🎯 Үйлчлүүлэгч олох арга, сошиал медиа маркетинг",
  "💬 Үйлчлүүлэгчтэй харилцах ур чадвар",
];

const PLANS = [
  {
    id: "6month" as Plan,
    duration: "6 сар",
    price: "89,000₮",
    originalPrice: "289,000₮",
    priceNum: 89000,
    qpayLink: "https://paylink.mn/api/s/inv_ms0whm9e0d21b1",
    badge: "🔥 Хямдралтай",
    color: "border-violet-500/40",
    headerBg: "bg-gradient-to-br from-violet-700 to-indigo-800",
    btnClass: "bg-violet-600 hover:bg-violet-700 text-white",
    features: [
      "6 сарын турш United Dream-ийг төлөөлөх эрх",
      "Нэг өдрийн онлайн сургалт + гэрчилгээ",
      "Өөрийн давтагдашгүй referral code",
      "Маркетингийн материал хангамж",
      "10–50% комисс",
      "6 сар идэвхтэй ажилласан нөхцөлд үнэгүй визний үйлчилгээ",
    ],
  },
  {
    id: "12month" as Plan,
    duration: "12 сар",
    price: "179,000₮",
    originalPrice: "479,000₮",
    priceNum: 179000,
    qpayLink: "https://paylink.mn/api/s/inv_ms0wjkei8a414d",
    badge: "⭐ Хамгийн ашигтай",
    color: "border-amber-400/60",
    headerBg: "bg-gradient-to-br from-amber-500 to-orange-600",
    btnClass: "bg-amber-500 hover:bg-amber-600 text-white",
    features: [
      "12 сарын турш United Dream-ийг төлөөлөх эрх",
      "Нэг өдрийн онлайн сургалт + гэрчилгээ",
      "Өөрийн давтагдашгүй referral code",
      "Маркетингийн материал хангамж",
      "10–50% комисс",
      "6 сар идэвхтэй ажилласан нөхцөлд үнэгүй визний үйлчилгээ",
      "АНУ-руу урилгатай аялалд оролцох боломж",
      "Гэрээт сургуулиудад хөнгөлөлттэй суралцах эрх",
    ],
  },
];

/* ─── Registration Modal ─────────────────────────────── */
function RegisterModal({
  plan,
  onClose,
}: {
  plan: (typeof PLANS)[0];
  onClose: () => void;
}) {
  const [form, setForm] = useState<RegForm>({ name: "", phone: "", email: "" });
  const [submitted, setSubmitted] = useState(false);
   const [paymentConfirmed, setPaymentConfirmed] = useState(false);
  const [confirmError, setConfirmError] = useState<string | null>(null);
  const confirmPaymentMutation = trpc.affiliate.confirmPayment.useMutation({
    onSuccess: () => {
      setPaymentConfirmed(true);
      setConfirmError(null);
    },
    onError: (err) => {
      setConfirmError(err.message || "Алдаа гарлаа. Дахин оролдоно уу.");
    },
  });

  const [showBankAffiliate, setShowBankAffiliate] = useState(false);
  const [copiedAffiliate, setCopiedAffiliate] = useState<string | null>(null);

  const copyAffiliate = (text: string, field: string) => {
    navigator.clipboard.writeText(text);
    setCopiedAffiliate(field);
    setTimeout(() => setCopiedAffiliate(null), 2000);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitted(true);
    confirmPaymentMutation.mutate({
      name: form.name,
      phone: form.phone,
      email: form.email,
      plan: plan.duration,
      price: plan.price,
    });
  };

  const handleConfirmPayment = () => {
    confirmPaymentMutation.mutate({
      name: form.name,
      phone: form.phone,
      email: form.email,
      plan: plan.duration,
      price: plan.price,
    });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
      <div className="rounded-2xl shadow-2xl w-full max-w-md overflow-hidden" style={{ background: "#1a1040" }}>
        {/* Header */}
        <div className={`${plan.headerBg} px-6 py-5 text-white`}>
          <div className="flex justify-between items-start">
            <div>
              <p className="text-sm opacity-80 mb-1">Affiliate агент болох</p>
              <h2 className="text-xl font-bold">{plan.duration}-ийн эрх</h2>
              <p className="text-2xl font-extrabold mt-1">{plan.price}</p>
            </div>
            <button
              onClick={onClose}
              className="text-white/70 hover:text-white text-2xl leading-none"
            >
              ×
            </button>
          </div>
        </div>

        <div className="p-6">
          {!submitted ? (
            <>
              <p className="text-sm text-white/60 mb-5">
                Мэдээллээ бөглөнө үү. Төлбөр хүлээн авсны дараа шууд ажиллах боломжтой
                агентийн нэвтрэх линк болон referral code имэйлаар илгээнэ.
              </p>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-semibold text-white/80 mb-1">
                    Таны нэр *
                  </label>
                  <input
                    required
                    type="text"
                    placeholder="Бат-Эрдэнэ Дорж"
                    value={form.name}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, name: e.target.value }))
                    }
                    className="w-full border border-white/20 rounded-lg px-4 py-2.5 text-sm text-white placeholder-white/40 bg-white/10 focus:outline-none focus:ring-2 focus:ring-violet-400"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-white/80 mb-1">
                    Утасны дугаар *
                  </label>
                  <input
                    required
                    type="tel"
                    placeholder="9911 2233"
                    value={form.phone}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, phone: e.target.value }))
                    }
                    className="w-full border border-white/20 rounded-lg px-4 py-2.5 text-sm text-white placeholder-white/40 bg-white/10 focus:outline-none focus:ring-2 focus:ring-violet-400"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-white/80 mb-1">
                    И-мэйл хаяг *
                  </label>
                  <input
                    required
                    type="email"
                    placeholder="example@gmail.com"
                    value={form.email}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, email: e.target.value }))
                    }
                    className="w-full border border-white/20 rounded-lg px-4 py-2.5 text-sm text-white placeholder-white/40 bg-white/10 focus:outline-none focus:ring-2 focus:ring-violet-400"
                  />
                </div>

                <Button type="submit" className={`w-full ${plan.btnClass} py-3`}>
                  Бүртгүүлэх
                </Button>
              </form>
            </>
          ) : (
            /* Payment info after submit */
            <div className="text-center">
              <p className="text-sm text-white/70 mb-4">
                Доорх QPay товч дарж төлбөрөө төлнө үү. Төлбөр хүлээн авсны дараа <strong className="text-white">{form.email}</strong> хаяг руу агентийн нэвтрэх линк очно.
              </p>
              {/* QPay primary button */}
              <a
                href={plan.qpayLink}
                target="_blank"
                rel="noopener noreferrer"
                className="w-full flex items-center justify-center gap-2 py-3.5 rounded-xl font-semibold text-white transition-all hover:opacity-90 active:scale-95 mb-1"
                style={{ background: "linear-gradient(135deg, #7c3aed, #4f46e5)", display: "flex" }}
              >
                <span>🔗</span>
                QPay-р төлж бүртгүүлэх — {plan.price}
              </a>
              <p className="text-center text-[11px] text-slate-400 mb-3">
                ⚠️ Гүйлгээний утга: <span className="text-slate-600 font-medium">утас, и-мэйл хаягаа</span>
              </p>

              {/* Bank transfer — always visible, prominent */}
              <div className="mt-3 rounded-2xl overflow-hidden" style={{ border: "2px solid #f59e0b", background: "linear-gradient(135deg, #fffbeb 0%, #fef3c7 100%)" }}>
                {/* Header */}
                <div className="px-4 py-3" style={{ background: "#f59e0b" }}>
                  <p className="text-white font-bold text-sm text-center">🏦 QPay ажиллахгүй бол — Дансаар шилжүүлнэ үү!</p>
                </div>
                {/* Bank details */}
                <div className="px-4 py-4 space-y-3">
                  {/* Account number */}
                  <div className="flex items-center justify-between rounded-xl px-3 py-2.5" style={{ background: "white", border: "1px solid #fde68a" }}>
                    <div>
                      <p className="text-[10px] uppercase tracking-wider font-semibold" style={{ color: "#92400e" }}>Дансны дугаар</p>
                      <p className="font-black text-xl tracking-widest" style={{ color: "#1e293b" }}>5175057440</p>
                    </div>
                    <button
                      onClick={() => copyAffiliate("5175057440", "acc")}
                      className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-medium transition-all"
                      style={{ background: copiedAffiliate === "acc" ? "#dcfce7" : "#fef9c3", color: copiedAffiliate === "acc" ? "#16a34a" : "#92400e", border: "1px solid " + (copiedAffiliate === "acc" ? "#86efac" : "#fde68a") }}
                    >
                      {copiedAffiliate === "acc" ? "✓ Хуулагдсан" : "📋 Хуулах"}
                    </button>
                  </div>
                  {/* IBAN */}
                  <div className="flex items-center justify-between rounded-xl px-3 py-2.5" style={{ background: "white", border: "1px solid #fde68a" }}>
                    <div>
                      <p className="text-[10px] uppercase tracking-wider font-semibold" style={{ color: "#92400e" }}>IBAN</p>
                      <p className="font-bold text-lg tracking-widest" style={{ color: "#1e293b" }}>67000500</p>
                    </div>
                    <button
                      onClick={() => copyAffiliate("67000500", "iban")}
                      className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-medium transition-all"
                      style={{ background: copiedAffiliate === "iban" ? "#dcfce7" : "#fef9c3", color: copiedAffiliate === "iban" ? "#16a34a" : "#92400e", border: "1px solid " + (copiedAffiliate === "iban" ? "#86efac" : "#fde68a") }}
                    >
                      {copiedAffiliate === "iban" ? "✓ Хуулагдсан" : "📋 Хуулах"}
                    </button>
                  </div>
                  {/* Bank name */}
                  <div className="rounded-xl px-3 py-2.5" style={{ background: "white", border: "1px solid #fde68a" }}>
                    <p className="text-[10px] uppercase tracking-wider font-semibold" style={{ color: "#92400e" }}>Банк</p>
                    <p className="font-bold text-sm" style={{ color: "#1e293b" }}>Хаан Банк</p>
                  </div>
                  {/* Account name */}
                  <div className="rounded-xl px-3 py-2.5" style={{ background: "white", border: "1px solid #fde68a" }}>
                    <p className="text-[10px] uppercase tracking-wider font-semibold" style={{ color: "#92400e" }}>Дансны нэр</p>
                    <p className="font-bold text-sm" style={{ color: "#1e293b" }}>Монголиан Юнайтэд Дрийм Консалтинг ХХК</p>
                  </div>
                  {/* Warning */}
                  <div className="rounded-xl px-3 py-2.5" style={{ background: "#fff7ed", border: "1px solid #fed7aa" }}>
                    <p className="text-xs font-bold" style={{ color: "#c2410c" }}>⚠️ Гүйлгээний утганд заавалбар:</p>
                    <p className="text-xs mt-0.5" style={{ color: "#9a3412" }}>Утасны дугаар болон и-мэйл хаягаа бичнэ үү</p>
                  </div>
                  {/* After transfer instruction */}
                  <p className="text-center text-xs" style={{ color: "#92400e" }}>
                    Шилжүүлсэний дараа <strong>uuganaa.be@gmail.com</strong> рүү имэйл илгээрэй бүртгэлт хүсэнэ үү
                  </p>
                </div>
              </div>


            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/* ─── Income Calculator ─────────────────────────────── */
function IncomeCalculator() {
  const [feePerClient, setFeePerClient] = useState(1500000);
  const [commissionRate, setCommissionRate] = useState(15);
  const [clientsPerMonth, setClientsPerMonth] = useState(3);

  const monthlyIncome = Math.round(feePerClient * (commissionRate / 100) * clientsPerMonth);
  const yearlyIncome = monthlyIncome * 12;

  const formatMNT = (val: number) =>
    val >= 1000000
      ? `${(val / 1000000).toFixed(val % 1000000 === 0 ? 0 : 1)}сая ₮`
      : `${(val / 1000).toFixed(0)}К ₮`;

  return (
    <section className="py-16 px-4" style={{ background: "#110d35" }}>
      <div className="max-w-3xl mx-auto">
        <div className="text-center mb-10">
          <span className="inline-block text-xs font-semibold px-3 py-1 rounded-full mb-3 uppercase tracking-wide" style={{ background: "rgba(124,58,237,0.2)", color: "#c4b5fd" }}>
            Орлогын тооцоолол
          </span>
          <h2 className="text-2xl md:text-3xl font-bold text-white">
            Та хэр их орлого олох вэ?
          </h2>
          <p className="mt-2 text-sm" style={{ color: "#a5b4fc" }}>
            Slider тохируулж өөрийн боломжит орлогоо тооцоол
          </p>
        </div>

        <div className="rounded-2xl p-6 md:p-8" style={{ background: "rgba(124,58,237,0.1)", border: "1px solid rgba(167,139,250,0.2)" }}>
          {/* Result display */}
          <div className="rounded-xl p-5 text-white text-center mb-8" style={{ background: "linear-gradient(135deg, #7c3aed, #6366f1)" }}>
            <p className="text-blue-200 text-sm mb-1">Сарын боломжит орлого</p>
            <p className="text-4xl md:text-5xl font-extrabold tracking-tight">
              {formatMNT(monthlyIncome)}
            </p>
            <p className="text-blue-200 text-sm mt-2">
              Жилийн нийт:{" "}
              <span className="text-white font-semibold">{formatMNT(yearlyIncome)}</span>
            </p>
          </div>

          {/* Sliders */}
          <div className="space-y-7">
            {/* Service fee slider */}
            <div>
              <div className="flex justify-between items-center mb-2">
                <label className="text-sm font-semibold text-slate-700">
                  Үйлчилгээний хөлс (нэг үйлчлүүлэгч)
                </label>
                <span className="text-blue-700 font-bold text-sm bg-blue-100 px-3 py-1 rounded-full">
                  {(feePerClient / 1000).toFixed(0)}К ₮
                </span>
              </div>
              <input
                type="range"
                min={650000}
                max={3000000}
                step={50000}
                value={feePerClient}
                onChange={(e) => setFeePerClient(Number(e.target.value))}
                className="w-full h-2 bg-slate-200 rounded-lg appearance-none cursor-pointer accent-blue-600"
              />
              <div className="flex justify-between text-xs text-slate-400 mt-1">
                <span>650К ₮</span>
                <span>3,000К ₮</span>
              </div>
            </div>

            {/* Commission rate slider */}
            <div>
              <div className="flex justify-between items-center mb-2">
                <label className="text-sm font-semibold text-slate-700">
                  Комиссын хувь
                </label>
                <span className="text-indigo-700 font-bold text-sm bg-indigo-100 px-3 py-1 rounded-full">
                  {commissionRate}%
                </span>
              </div>
              <input
                type="range"
                min={10}
                max={50}
                step={1}
                value={commissionRate}
                onChange={(e) => setCommissionRate(Number(e.target.value))}
                className="w-full h-2 bg-slate-200 rounded-lg appearance-none cursor-pointer accent-indigo-600"
              />
              <div className="flex justify-between text-xs text-slate-400 mt-1">
                <span>10%</span>
                <span>50%</span>
              </div>
            </div>

            {/* Clients per month slider */}
            <div>
              <div className="flex justify-between items-center mb-2">
                <label className="text-sm font-semibold text-slate-700">
                  Сарын үйлчлүүлэгчийн тоо
                </label>
                <span className="text-green-700 font-bold text-sm bg-green-100 px-3 py-1 rounded-full">
                  {clientsPerMonth} хүн
                </span>
              </div>
              <input
                type="range"
                min={1}
                max={20}
                step={1}
                value={clientsPerMonth}
                onChange={(e) => setClientsPerMonth(Number(e.target.value))}
                className="w-full h-2 bg-slate-200 rounded-lg appearance-none cursor-pointer accent-green-600"
              />
              <div className="flex justify-between text-xs text-slate-400 mt-1">
                <span>1 хүн</span>
                <span>20 хүн</span>
              </div>
            </div>
          </div>

          {/* Breakdown */}
          <div className="mt-7 bg-white rounded-xl border border-slate-100 p-4">
            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-3">Тооцооллын задаргаа</p>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-slate-500">Үйлчилгээний хөлс</span>
                <span className="font-semibold text-slate-700">{(feePerClient / 1000).toFixed(0)}К ₮</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-500">× Комиссын хувь</span>
                <span className="font-semibold text-slate-700">{commissionRate}%</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-500">× Сарын үйлчлүүлэгч</span>
                <span className="font-semibold text-slate-700">{clientsPerMonth} хүн</span>
              </div>
              <div className="flex justify-between border-t border-slate-100 pt-2 mt-2">
                <span className="font-bold text-slate-800">= Сарын орлого</span>
                <span className="font-extrabold text-blue-700">{formatMNT(monthlyIncome)}</span>
              </div>
            </div>
          </div>

          <p className="text-xs text-slate-400 mt-4 text-center">
            * Тооцоолол зөвхөн лавлагааны зорилготой. Бодит орлого таны идэвхи, сүлжээнээс хамаарна.
          </p>
        </div>
      </div>
    </section>
  );
}

/* ─── Main Page ──────────────────────────────────────── */
/* ─── Midnight Countdown (Mongolia UTC+8) ───────────────────────── */
function useMidnightCountdown() {
  const getMsUntilMidnightUB = () => {
    // Mongolia Standard Time = UTC+8
    const now = new Date();
    const ubOffset = 8 * 60; // UTC+8 in minutes
    const localOffset = -now.getTimezoneOffset(); // local offset in minutes
    const diffMs = (ubOffset - localOffset) * 60 * 1000;
    const ubNow = new Date(now.getTime() + diffMs);
    const midnight = new Date(ubNow);
    midnight.setHours(24, 0, 0, 0);
    return midnight.getTime() - ubNow.getTime();
  };

  const [ms, setMs] = useState(getMsUntilMidnightUB);

  useEffect(() => {
    const id = setInterval(() => setMs(getMsUntilMidnightUB()), 1000);
    return () => clearInterval(id);
  }, []);

  const h = Math.floor(ms / 3600000);
  const m = Math.floor((ms % 3600000) / 60000);
  const s = Math.floor((ms % 60000) / 1000);
  const pad = (n: number) => String(n).padStart(2, "0");
  return { h: pad(h), m: pad(m), s: pad(s), ms };
}

function MidnightCountdownBanner() {
  const { h, m, s } = useMidnightCountdown();
  return (
    <div className="relative overflow-hidden rounded-2xl mb-8 bg-gradient-to-r from-red-600 via-orange-500 to-amber-500 p-[2px] shadow-xl">
      <div className="bg-gradient-to-r from-slate-900 via-red-950 to-slate-900 rounded-2xl px-6 py-5">
        <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="text-center sm:text-left">
            <div className="flex items-center gap-2 justify-center sm:justify-start mb-1">
              <span className="inline-block w-2 h-2 rounded-full bg-red-400 animate-ping" />
              <span className="text-red-400 text-xs font-bold uppercase tracking-widest">Одоо хямдралтай</span>
            </div>
            <p className="text-white font-extrabold text-lg md:text-xl leading-tight">
              Монголын цагаар шөнийн 12 цаг хүртэл хямдралтай!
            </p>
            <p className="text-slate-400 text-xs mt-1">Хугацаа дууссанаар үнэ эргээд буцаана</p>
          </div>
          <div className="flex items-center gap-2">
            {[{ val: h, label: "Цаг" }, { val: m, label: "Мин" }, { val: s, label: "Сек" }].map(({ val, label }, i) => (
              <React.Fragment key={label}>
                <div className="flex flex-col items-center">
                  <div className="bg-white/10 backdrop-blur border border-white/20 rounded-xl w-16 h-16 flex items-center justify-center">
                    <span className="text-white font-black text-2xl tabular-nums">{val}</span>
                  </div>
                  <span className="text-slate-400 text-xs mt-1">{label}</span>
                </div>
                {i < 2 && <span className="text-white font-black text-2xl mb-5 opacity-60">:</span>}
              </React.Fragment>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

export default function AffiliatePage() {
  const [selectedPlan, setSelectedPlan] = useState<(typeof PLANS)[0] | null>(
    null
  );

  return (
    <div className="min-h-screen" style={{ background: "#0D0A2E" }}>
      <Header />

      {/* ── Hero ── */}
      <section className="text-white relative overflow-hidden" style={{ background: "linear-gradient(135deg, #0D0A2E 0%, #1a0a4e 50%, #0D0A2E 100%)", minHeight: "100vh" }}>
        {/* Floating violet-tinted icons */}
        <AffiliateFloatingIcons />

        {/* Gradient orbs */}
        <div className="absolute inset-0 pointer-events-none">
          <motion.div
            className="absolute rounded-full"
            style={{ width: 700, height: 700, background: "radial-gradient(circle, rgba(124,58,237,0.2) 0%, transparent 70%)", top: "-20%", left: "-10%" }}
            animate={{ x: [0, 55, 0], y: [0, 40, 0] }}
            transition={{ duration: 20, repeat: Infinity, ease: "easeInOut" }}
          />
          <motion.div
            className="absolute rounded-full"
            style={{ width: 500, height: 500, background: "radial-gradient(circle, rgba(79,70,229,0.16) 0%, transparent 70%)", top: "30%", right: "-5%" }}
            animate={{ x: [0, -45, 0], y: [0, -55, 0] }}
            transition={{ duration: 17, repeat: Infinity, ease: "easeInOut", delay: 3 }}
          />
        </div>

        {/* Content: two-column layout */}
        <div className="relative z-10 max-w-6xl mx-auto px-4 pt-28 pb-16 flex flex-col lg:flex-row items-center gap-12 lg:gap-16">

          {/* LEFT: Copy */}
          <div className="flex-1 text-center lg:text-left">
            {/* Badge */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <span
                className="inline-flex items-center gap-2 text-xs font-bold px-4 py-2 rounded-full mb-6 tracking-wide uppercase"
                style={{ background: "rgba(124,58,237,0.25)", border: "1px solid rgba(167,139,250,0.4)", color: "#c4b5fd" }}
              >
                <span className="w-2 h-2 rounded-full bg-green-400 animate-pulse inline-block" />
                Онлайн Affiliate Агент — Нээлттэй байна
              </span>
            </motion.div>

            {/* Headline */}
            <motion.h1
              className="text-4xl md:text-5xl lg:text-6xl font-extrabold leading-tight mb-6"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.1 }}
            >
              Гэрээсээ{" "}
              <span style={{ background: "linear-gradient(90deg, #a78bfa, #818cf8, #60a5fa)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>
                орлого ол
              </span>
              <br />
              <span className="text-3xl md:text-4xl lg:text-5xl" style={{ color: "#e2d9f3" }}>
                сард{" "}
                <span style={{ background: "linear-gradient(90deg, #fbbf24, #f59e0b)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>
                  3,000,000₮
                </span>
                {" "}хүртэл
              </span>
            </motion.h1>

            {/* Sub */}
            <motion.p
              className="text-lg md:text-xl mb-8 max-w-xl"
              style={{ color: "#a5b4fc", lineHeight: 1.7 }}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.2 }}
            >
              United Dream Mongolia-ийн албан ёсны агент болж,{" "}
              <strong className="text-white">цаг, байршил харгалзахгүй</strong>{" "}
              онлайнаар ажиллана уу. Нэг өдрийн сургалт — маргаашнаас шууд эхэлнэ.
            </motion.p>

            {/* Social proof stats */}
            <motion.div
              className="flex flex-wrap gap-4 mb-8 justify-center lg:justify-start"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.3 }}
            >
              {[
                { num: "10–50%", label: "Комиссын хувь" },
                { num: "1 өдөр", label: "Сургалтын хугацаа" },
                { num: "24 цаг", label: "Эрх идэвхжих" },
              ].map((s) => (
                <div
                  key={s.label}
                  className="rounded-xl px-5 py-3 text-center"
                  style={{ background: "rgba(124,58,237,0.18)", border: "1px solid rgba(167,139,250,0.25)" }}
                >
                  <p className="text-xl font-extrabold text-white">{s.num}</p>
                  <p className="text-xs mt-0.5" style={{ color: "#c4b5fd" }}>{s.label}</p>
                </div>
              ))}
            </motion.div>

            {/* Urgency */}
            <motion.div
              className="inline-flex items-center gap-2 rounded-xl px-4 py-2.5 mb-8"
              style={{ background: "rgba(239,68,68,0.12)", border: "1px solid rgba(239,68,68,0.3)" }}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.7, delay: 0.4 }}
            >
              <span className="w-2 h-2 rounded-full bg-red-400 animate-ping inline-block" />
              <span className="text-sm font-semibold" style={{ color: "#fca5a5" }}>
                Зөвхөн <strong className="text-white">20 агентийн</strong> зай үлдсэн байна
              </span>
            </motion.div>

            {/* CTAs */}
            <motion.div
              className="flex flex-col sm:flex-row gap-3 justify-center lg:justify-start"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.5 }}
            >
              <button
                onClick={() => document.getElementById("pricing")?.scrollIntoView({ behavior: "smooth" })}
                className="font-bold px-8 py-4 rounded-xl transition-all text-white text-base shadow-lg hover:scale-105"
                style={{ background: "linear-gradient(135deg, #7c3aed, #6366f1)", boxShadow: "0 0 30px rgba(124,58,237,0.4)" }}
              >
                🚀 Агент болох →
              </button>
              <button
                onClick={() => document.getElementById("how-it-works")?.scrollIntoView({ behavior: "smooth" })}
                className="font-semibold px-8 py-4 rounded-xl transition-colors text-white text-base"
                style={{ border: "1px solid rgba(167,139,250,0.3)", background: "rgba(124,58,237,0.1)" }}
              >
                Хэрхэн ажилладаг вэ?
              </button>
            </motion.div>
          </div>

          {/* RIGHT: Woman image */}
          <motion.div
            className="flex-shrink-0 relative"
            initial={{ opacity: 0, x: 40 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.3 }}
          >
            {/* Glow behind image */}
            <div
              className="absolute inset-0 rounded-3xl blur-3xl"
              style={{ background: "radial-gradient(circle, rgba(124,58,237,0.45) 0%, transparent 70%)", transform: "scale(1.1)" }}
            />
            {/* Floating success notification */}
            <motion.div
              className="absolute -top-4 -left-4 z-20 rounded-2xl px-4 py-3 shadow-xl"
              style={{ background: "rgba(13,10,46,0.92)", border: "1px solid rgba(167,139,250,0.35)", backdropFilter: "blur(12px)" }}
              animate={{ y: [0, -8, 0] }}
              transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
            >
              <div className="flex items-center gap-2">
                <span className="text-2xl">✅</span>
                <div>
                  <p className="text-xs font-semibold" style={{ color: "#a78bfa" }}>Агент баталгаажлаа!</p>
                  <p className="text-base font-extrabold text-white">United Dream</p>
                </div>
              </div>
            </motion.div>
            {/* Floating referral badge */}
            <motion.div
              className="absolute -bottom-4 -right-4 z-20 rounded-2xl px-4 py-3 shadow-xl"
              style={{ background: "rgba(13,10,46,0.92)", border: "1px solid rgba(167,139,250,0.35)", backdropFilter: "blur(12px)" }}
              animate={{ y: [0, 8, 0] }}
              transition={{ duration: 3.5, repeat: Infinity, ease: "easeInOut", delay: 1 }}
            >
              <div className="flex items-center gap-2">
                <span className="text-2xl">🔑</span>
                <div>
                  <p className="text-xs font-semibold" style={{ color: "#a78bfa" }}>Referral code</p>
                  <p className="text-base font-extrabold text-white">UD-SARAA</p>
                </div>
              </div>
            </motion.div>
            {/* The woman image */}
            <div
              className="relative rounded-3xl overflow-hidden"
              style={{ width: 320, maxWidth: "100%", border: "2px solid rgba(167,139,250,0.3)", boxShadow: "0 0 60px rgba(124,58,237,0.3)" }}
            >
              <img
                src="https://d2xsxph8kpxj0f.cloudfront.net/310519663375899735/N7ycErHKT5joBFzKWLPbGM/affiliate-hero-woman-SZJnHLhqgu7fHjkUqjuADH.webp"
                alt="Гэрээсээ ажиллаж байгаа Монгол эмэгтэй"
                className="w-full h-auto block"
                style={{ display: "block" }}
              />
              {/* Bottom gradient fade */}
              <div
                className="absolute bottom-0 left-0 right-0 h-24"
                style={{ background: "linear-gradient(to top, #0D0A2E 0%, transparent 100%)" }}
              />
            </div>
          </motion.div>
        </div>

        {/* Bottom wave fade */}
        <div
          className="absolute bottom-0 left-0 right-0 h-20 pointer-events-none"
          style={{ background: "linear-gradient(to bottom, transparent, #110d35)" }}
        />
      </section>

      {/* ── Why Become an Agent ── */}
      <section className="py-16 px-4" style={{ background: "#0a0720" }}>
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-10">
            <span className="inline-block text-xs font-bold uppercase tracking-widest px-4 py-1.5 rounded-full mb-4" style={{ background: "rgba(124,58,237,0.2)", color: "#a78bfa" }}>Зах зээлийн боломж</span>
            <h2 className="text-2xl md:text-3xl font-bold text-white">
              Яагаад онлайн агент болох вэ?
            </h2>
            <p className="mt-2 text-base" style={{ color: "#a5b4fc" }}>
              Монголын виз зуучлалын зах зээл асар том — таны боломж энд байна
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-10">
            {/* Stat 1 */}
            <div className="rounded-2xl p-6 text-center" style={{ background: "linear-gradient(135deg, rgba(124,58,237,0.15), rgba(99,102,241,0.1))", border: "1px solid rgba(167,139,250,0.25)" }}>
              <div className="text-4xl font-black text-white mb-1">100,000<span className="text-violet-400">+</span></div>
              <div className="text-sm font-semibold text-violet-300 mb-2">хүн жил бүр</div>
              <p className="text-xs leading-relaxed" style={{ color: "#c4b5fd" }}>Монголоос гадаадад виз мэдүүлдэг иргэдийн тоо жил бүр өсөн нэмэгдэж байна</p>
            </div>
            {/* Stat 2 */}
            <div className="rounded-2xl p-6 text-center" style={{ background: "linear-gradient(135deg, rgba(124,58,237,0.15), rgba(99,102,241,0.1))", border: "1px solid rgba(167,139,250,0.25)" }}>
              <div className="text-4xl font-black text-white mb-1">50<span className="text-violet-400">тэрбум₮</span></div>
              <div className="text-sm font-semibold text-violet-300 mb-2">хүртэлх зах зээл</div>
              <p className="text-xs leading-relaxed" style={{ color: "#c4b5fd" }}>Монголчууд виз зуучлалын үйлчилгээнд жил бүр нийт 50 тэрбум хүртэлх төгрөг төлдөг</p>
            </div>
            {/* Stat 3 */}
            <div className="rounded-2xl p-6 text-center" style={{ background: "linear-gradient(135deg, rgba(124,58,237,0.15), rgba(99,102,241,0.1))", border: "1px solid rgba(167,139,250,0.25)" }}>
              <div className="text-4xl font-black text-white mb-1">80<span className="text-violet-400">%</span></div>
              <div className="text-sm font-semibold text-violet-300 mb-2">мэдэхгүй байдаг</div>
              <p className="text-xs leading-relaxed" style={{ color: "#c4b5fd" }}>Виз хэрхэн мэдүүлэхээ мэддэггүй хүмүүс — тэд таны тусламжийг хүлээж байна</p>
            </div>
          </div>
          {/* Additional advantages */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {[
              { icon: "🏠", title: "Гэрээсээ ажиллана", desc: "Оффис, цаг, байршил харгалзахгүй. Утас байхад л болно." },
              { icon: "📈", title: "Зах зээл өсч байна", desc: "Гадаадад суралцах, ажиллах, аялах монголчуудын тоо жил бүр нэмэгдэж байна." },
              { icon: "🎓", title: "Мэргэжлийн сургалт", desc: "Нэг өдрийн сургалтаар бүх мэдлэгийг эзэмшиж, маргаашнаас ажиллана." },
              { icon: "💼", title: "Бэлэн систем", desc: "Маркетингийн материал, referral код, дэмжлэг — бүгдийг бид бэлдэж өгнө." },
              { icon: "🔑", title: "Ховор визний боломж", desc: "Хүн бүрийн мэдээд байдаггүй ховор визний боломжуудыг агент болсноор эхэлж мэдэж, үйлчлүүлэгчдэдээ санал болгох давуу эрх эдэлнэ." },
            ].map((item) => (
              <div key={item.title} className="flex items-start gap-4 rounded-xl p-4" style={{ background: "rgba(124,58,237,0.08)", border: "1px solid rgba(167,139,250,0.15)" }}>
                <span className="text-2xl mt-0.5">{item.icon}</span>
                <div>
                  <h4 className="font-bold text-white text-sm mb-1">{item.title}</h4>
                  <p className="text-xs leading-relaxed" style={{ color: "#c4b5fd" }}>{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Benefits ── */}
      <section className="py-16 px-4" style={{ background: "#110d35" }}>
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-10">
            <h2 className="text-2xl md:text-3xl font-bold text-white">
              Танд ямар өгөөжтэй вэ?
            </h2>
            <p className="mt-2" style={{ color: "#a5b4fc" }}>
              Зөвхөн орлого биш — амьдралыг өөрчлөх боломж
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 items-start">
            {BENEFITS.map((b) => (
              <div
                key={b.title}
                className="rounded-xl p-5 hover:shadow-md transition-shadow"
                style={{ background: "rgba(124,58,237,0.1)", border: "1px solid rgba(167,139,250,0.2)" }}
              >
                <div className="text-3xl mb-3">{b.icon}</div>
                <h3 className="font-bold text-white mb-1.5">{b.title}</h3>
                <p className="text-sm leading-relaxed" style={{ color: "#c4b5fd" }}>
                  {b.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── How it works ── */}
      <section id="how-it-works" className="py-16 px-4" style={{ background: "#0D0A2E" }}>
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-10">
            <h2 className="text-2xl md:text-3xl font-bold text-white">
              Хэрхэн ажилладаг вэ?
            </h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {HOW_IT_WORKS.map((s) => (
              <div key={s.step} className="text-center">
                <div className="w-14 h-14 text-white rounded-2xl flex items-center justify-center text-xl font-extrabold mx-auto mb-4" style={{ background: "linear-gradient(135deg, #7c3aed, #6366f1)" }}>
                  {s.step}
                </div>
                <h3 className="font-bold text-white mb-2">{s.title}</h3>
                <p className="text-sm leading-relaxed" style={{ color: "#a5b4fc" }}>
                  {s.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Referral code explainer ── */}
      <section className="py-14 px-4 text-white" style={{ background: "linear-gradient(135deg, #7c3aed, #6366f1)" }}>
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-2xl md:text-3xl font-bold mb-4">
            Referral code гэж юу вэ?
          </h2>
          <p className="text-blue-100 text-base leading-relaxed mb-6">
            Зөвхөн таньд зориулсан давтагдашгүй код. Таны кодоор ирсэн
            үйлчлүүлэгч{" "}
            <strong className="text-white">
              хөнгөлөлттэй үйлчилгээ авах
            </strong>{" "}
            боломжтой болох бөгөөд та{" "}
            <strong className="text-white">үйлчилгээний хөлсний 10–50%</strong>
            -ийг шууд авна.
          </p>
          <div className="inline-flex items-center gap-3 bg-white/10 border border-white/20 rounded-2xl px-6 py-4">
            <span className="text-3xl">🔑</span>
            <div className="text-left">
              <p className="text-xs text-blue-200 uppercase tracking-wide font-semibold">
                Жишээ код
              </p>
              <p className="text-2xl font-mono font-bold tracking-widest">
                UD-BATAA
              </p>
            </div>
          </div>
          <p className="text-blue-200 text-sm mt-4">
            Та өөрийн нэрийн дагуу тусгай код авна
          </p>
        </div>
      </section>

      {/* ── Income Calculator ── */}
      <IncomeCalculator />

      {/* ── Training curriculum ── */}
      <section className="py-16 px-4" style={{ background: "#110d35" }}>
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-10">
            <h2 className="text-2xl md:text-3xl font-bold text-white">
              Сургалтанд юу орох вэ?
            </h2>
            <p className="mt-2" style={{ color: "#a5b4fc" }}>
              Нэг өдрийн онлайн сургалт — бүх мэдлэгийг нэг дор
            </p>
          </div>
          <div className="rounded-2xl overflow-hidden" style={{ border: "1px solid rgba(167,139,250,0.2)" }}>
            <div className="px-6 py-4" style={{ background: "linear-gradient(135deg, #7c3aed, #6366f1)" }}>
              <p className="text-white font-semibold text-sm">
                📚 Сургалтын хөтөлбөр
              </p>
            </div>
            <div style={{ background: "rgba(124,58,237,0.08)" }}>
              {CURRICULUM.map((item, i) => (
                <div key={i} className="px-6 py-3.5 text-sm flex items-start gap-3" style={{ borderBottom: "1px solid rgba(167,139,250,0.1)", color: "#e2d9f3" }}>
                  <span className="font-mono text-xs mt-0.5 shrink-0" style={{ color: "#7c3aed" }}>
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <span>{item}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── Pricing ── */}
      <section id="pricing" className="py-16 px-4" style={{ background: "#0D0A2E" }}>
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-8">
            <h2 className="text-2xl md:text-3xl font-bold text-white">
              Агент болох
            </h2>
            <p className="mt-2" style={{ color: "#a5b4fc" }}>
              Хугацаагаа сонгоод бүртгүүлнэ үү
            </p>
          </div>
          <MidnightCountdownBanner />
          <div className="grid sm:grid-cols-2 gap-6">
            {PLANS.map((plan) => (
              <div
                key={plan.id}
                className={`relative rounded-2xl border-2 ${plan.color} overflow-hidden shadow-sm hover:shadow-lg transition-shadow`}
              >
                {plan.badge && (
                  <div className="absolute -top-1 left-1/2 -translate-x-1/2 bg-gradient-to-r from-red-500 to-orange-500 text-white text-xs font-bold px-4 py-1.5 rounded-full shadow-lg whitespace-nowrap z-10">
                    {plan.badge}
                  </div>
                )}
                <div className={`${plan.headerBg} px-6 pt-8 pb-5 text-white`}>
                  <p className="text-sm opacity-80 mb-1">Төлөөлөх эрх</p>
                  <h3 className="text-2xl font-extrabold">{plan.duration}</h3>
                  <div className="flex items-end gap-3 mt-2">
                    <p className="text-3xl font-extrabold">{plan.price}</p>
                    <p className="text-base line-through opacity-60 mb-1">{plan.originalPrice}</p>
                  </div>
                  <div className="mt-2 inline-flex items-center gap-1.5 bg-white/20 backdrop-blur rounded-full px-3 py-1">
                    <span className="text-xs font-bold">🔥 Одоо хямдралтай — шөнийн 12 цаг хүртэл</span>
                  </div>
                </div>
                <div className="p-6" style={{ background: "rgba(13,10,46,0.95)" }}>
                  <ul className="space-y-2.5 mb-6">
                    {plan.features.map((f, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm" style={{ color: "#e2d9f3" }}>
                        <span className="mt-0.5 shrink-0" style={{ color: "#a78bfa" }}>✓</span>
                        <span>{f}</span>
                      </li>
                    ))}
                  </ul>
                  <button
                    onClick={() => setSelectedPlan(plan)}
                    className={`w-full ${plan.btnClass} font-semibold py-3 rounded-xl transition-colors`}
                  >
                    Бүртгүүлэх — {plan.price}
                  </button>
                </div>
              </div>
            ))}
          </div>
          <p className="text-center text-xs mt-6" style={{ color: "#7c6fa0" }}>
            * Сургалт нэг өдрийн онлайн хэлбэртэй. Бүртгэлийн дараа менежер
            24 цагийн дотор холбогдоно.
          </p>
        </div>
      </section>

      <Footer />

      {/* ── Modal ── */}
      {selectedPlan && (
        <RegisterModal
          plan={selectedPlan}
          onClose={() => setSelectedPlan(null)}
        />
      )}
    </div>
  );
}
